/**
 * Shared helpers for the MeetingSummary view.
 */

import { formatClock } from "../../lib/format-date"

/** mm:ss timestamp — thin alias over the shared {@link formatClock}. */
export function fmtTimestamp(seconds: number): string {
  return formatClock(seconds)
}

/** Compute talk-time percentages from a parallel `speaker_names` + `talk_share` array (seconds). */
export function talkPercent(speakerNames: string[], talkShare?: number[]): Map<string, number> {
  if (!talkShare || talkShare.length === 0) return new Map()
  const total = talkShare.reduce((a, b) => a + b, 0)
  if (total <= 0) return new Map()
  // Sum seconds per NAME before computing percentages, so a person split across
  // more than one roster slot (e.g. a far-end echo merged into a room speaker by
  // rename) collapses into a single row instead of being silently dropped by
  // last-write-wins. Auto-merged meetings have no duplicate names; this covers
  // the manual merge-by-rename path.
  const secondsByName = new Map<string, number>()
  speakerNames.forEach((name, i) => {
    secondsByName.set(name, (secondsByName.get(name) ?? 0) + (talkShare[i] ?? 0))
  })
  const result = new Map<string, number>()
  for (const [name, seconds] of secondsByName) {
    result.set(name, Math.round((seconds / total) * 100))
  }
  return result
}

/** Best-effort timestamp lookup for a quote — finds the first streaming segment whose
 * speaker matches and whose text contains the quote (or is contained by it). */
export function findQuoteTimestamp(
  quote: string,
  speaker: string,
  segments: { speaker: string; text: string; start: number }[] | undefined,
): number | null {
  if (!segments) return null
  const needle = quote.toLowerCase().trim()
  if (!needle) return null
  for (const seg of segments) {
    if (seg.speaker !== speaker) continue
    const hay = (seg.text ?? "").toLowerCase()
    if (hay.includes(needle) || needle.includes(hay)) return seg.start
  }
  return null
}

type TSeekSegment = { speaker: string; text: string; start: number }

/**
 * Low-signal short words (EN + NL) that say nothing about *where* in the
 * recording a point lives. Filtering them keeps distinctive content words in
 * control of the match so a paraphrased point still lands near its moment.
 */
const SEEK_STOPWORDS = new Set([
  // English
  "that",
  "this",
  "with",
  "have",
  "will",
  "from",
  "they",
  "them",
  "their",
  "what",
  "when",
  "which",
  "would",
  "could",
  "should",
  "about",
  "into",
  "over",
  "also",
  "than",
  "then",
  "there",
  "were",
  "been",
  "just",
  "like",
  "some",
  "more",
  "your",
  "yours",
  "ours",
  "because",
  "while",
  "where",
  "very",
  // Dutch
  "dat",
  "deze",
  "met",
  "voor",
  "zijn",
  "wordt",
  "worden",
  "naar",
  "over",
  "ook",
  "maar",
  "want",
  "door",
  "veel",
  "gaan",
  "gaat",
  "moet",
  "kunnen",
  "hebben",
  "heeft",
  "omdat",
  "terwijl",
  "waar",
  "heel",
  "onze",
  "jouw",
  "jullie",
])

interface ISeekIndex {
  segs: { start: number; terms: Set<string> }[]
  /** Inverse document frequency per term — rarer terms weigh more, so a shared
   * distinctive word ("OpenTelemetry") beats a dozen shared filler words. */
  idf: Map<string, number>
}

// Tokenisation + IDF over a meeting's segments is reused across every card, and
// the same `segments` array reference is handed to all of them, so memoise the
// derived index per array. WeakMap → no leak when the meeting unmounts.
const seekIndexCache = new WeakMap<object, ISeekIndex>()

function seekTokens(text: string): string[] {
  return text
    .toLowerCase()
    .split(/\W+/)
    .filter((w) => w.length >= 4 && !SEEK_STOPWORDS.has(w))
}

function buildSeekIndex(segments: TSeekSegment[]): ISeekIndex {
  const segs = segments.map((s) => ({ start: s.start, terms: new Set(seekTokens(s.text)) }))
  const df = new Map<string, number>()
  for (const s of segs) for (const term of s.terms) df.set(term, (df.get(term) ?? 0) + 1)
  const n = segs.length || 1
  const idf = new Map<string, number>()
  for (const [term, d] of df) idf.set(term, Math.log(1 + n / d))
  return { segs, idf }
}

function getSeekIndex(segments: TSeekSegment[]): ISeekIndex {
  const cached = seekIndexCache.get(segments)
  if (cached) return cached
  const built = buildSeekIndex(segments)
  seekIndexCache.set(segments, built)
  return built
}

/**
 * Best-match segment start time for an arbitrary piece of analysis text
 * (decision, action item, open question) — used to wire "jump to this moment"
 * on items not tied to a specific speaker (so the speaker field is ignored,
 * unlike `findQuoteTimestamp`).
 *
 * Heuristic, paraphrase-tolerant fallback for when the pipeline didn't emit a
 * real per-point offset: distinctive content terms (IDF-weighted, stopwords
 * removed) of the point are matched against a sliding two-segment window — so a
 * point synthesised across a speaker turn still resolves — and the best window
 * wins when the weighted overlap clears a precision threshold.
 */
export function findSeekTime(text: string, segments: TSeekSegment[] | undefined): number | null {
  if (!segments?.length) return null
  const terms = seekTokens(text)
  if (!terms.length) return null

  const { segs, idf } = getSeekIndex(segments)
  const fallbackIdf = Math.log(1 + segs.length)
  const weightOf = (term: string): number => idf.get(term) ?? fallbackIdf
  const totalWeight = terms.reduce((sum, term) => sum + weightOf(term), 0)
  if (totalWeight <= 0) return null

  // Matched IDF weight of the point's terms within one segment.
  const matchedWeight = (seg: { terms: Set<string> }): number => {
    let sum = 0
    for (const term of terms) if (seg.terms.has(term)) sum += weightOf(term)
    return sum
  }

  // Anchor on the single segment with the strongest match…
  let bestScore = 0
  let bestTime: number | null = null
  for (const seg of segs) {
    const score = matchedWeight(seg) / totalWeight
    if (score > bestScore) {
      bestScore = score
      bestTime = seg.start
    }
  }
  // …but let a point synthesised across a turn boundary clear the bar via a
  // two-segment window, anchored to whichever half carries more of the match.
  for (let i = 0; i + 1 < segs.length; i++) {
    const a = segs[i]
    const b = segs[i + 1]
    const windowTerms = new Set([...a.terms, ...b.terms])
    let matched = 0
    for (const term of terms) if (windowTerms.has(term)) matched += weightOf(term)
    const score = matched / totalWeight
    if (score > bestScore) {
      bestScore = score
      bestTime = matchedWeight(b) > matchedWeight(a) ? b.start : a.start
    }
  }
  // Require the distinctive terms of the point to genuinely co-occur in a
  // window — tuned for precision so a chip never jumps somewhere unrelated.
  return bestScore >= 0.4 ? bestTime : null
}

/**
 * Resolve a seekable timestamp for an analysis point, preferring a real
 * per-point offset emitted by the pipeline (`analysis.seek_offsets`, in
 * seconds) and falling back to the heuristic `findSeekTime` matcher when the
 * offset is absent or sentinel (-1) — e.g. for meetings analysed before
 * per-point offsets shipped, or compacted long sessions.
 */
export function resolveSeekTime(
  offset: number | undefined,
  text: string,
  segments: TSeekSegment[] | undefined,
): number | null {
  if (typeof offset === "number" && offset >= 0) return offset
  return findSeekTime(text, segments)
}
